/**
 * 🌀 BackdropLoading - Tela de Carregamento Global
 * 
 * Design moderno com animação Quantum
 * 
 * @author Sistema
 * @since 2025-12-06
 */

import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import GoogleLoader from "../ui/GoogleLoader";
import ColorModeContext from "../../layout/themeContext";

const useStyles = makeStyles(() => ({
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 32,
    backgroundColor: (props) => props.isDark ? "#0b141a" : "#eae6df",
    animation: "$fadeIn 0.3s ease-out",
  },

  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 32,
    padding: 40,
    borderRadius: 24,
    background: (props) => props.isDark 
      ? "rgba(255, 255, 255, 0.03)" 
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(20px)",
    border: (props) => props.isDark 
      ? "1px solid rgba(255, 255, 255, 0.08)" 
      : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: (props) => props.isDark 
      ? "0 25px 80px rgba(0, 0, 0, 0.5)" 
      : "0 25px 80px rgba(0, 0, 0, 0.1)",
    animation: "$slideUp 0.5s ease-out",
  },

  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    background: "linear-gradient(135deg, #a78bfa 0%, #ec4899 50%, #fbbf24 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textAlign: "center",
    letterSpacing: "1px",
  },

  message: {
    fontSize: "1rem",
    fontWeight: 400,
    color: (props) => props.isDark 
      ? "rgba(255, 255, 255, 0.8)" 
      : "rgba(0, 0, 0, 0.7)",
    textAlign: "center",
    animation: "$pulse 2s ease-in-out infinite",
  },

  timeoutMessage: {
    fontSize: "0.85rem",
    color: (props) => props.isDark 
      ? "rgba(255, 255, 255, 0.5)" 
      : "rgba(0, 0, 0, 0.5)",
    textAlign: "center",
    marginTop: 8,
    animation: "$fadeIn 0.3s ease-out",
  },

  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },

  "@keyframes slideUp": {
    "0%": { opacity: 0, transform: "translateY(20px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },

  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.6 },
    "50%": { opacity: 1 },
  },
}));

// ⏱️ Timeout de segurança (15 segundos)
const LOADING_TIMEOUT = 15000;

const BackdropLoading = ({
  message = "Carregando...",
  title = "",
  showTitle = false,
  logoUrl = null,
}) => {
  const history = useHistory();
  const colorModeContext = useContext(ColorModeContext);
  const [showTimeout, setShowTimeout] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [logo, setLogo] = useState(null);

  // Detectar tema atual e logo
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute("data-theme");
      setIsDark(theme === "dark");
      
      // Obter logo do contexto baseado no tema
      if (colorModeContext) {
        const currentLogo = theme === "dark" 
          ? colorModeContext.appLogoDark 
          : colorModeContext.appLogoLight;
        setLogo(currentLogo || logoUrl);
      } else {
        setLogo(logoUrl);
      }
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, [colorModeContext, logoUrl]);

  const classes = useStyles({ isDark });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTimeout(true);
      console.warn("⏱️ [BackdropLoading] Timeout de carregamento");

      const token = localStorage.getItem("token");
      if (!token) {
        if (
          history.location.pathname !== "/login" &&
          history.location.pathname !== "/signup"
        ) {
          history.push("/login");
        }
      }
    }, LOADING_TIMEOUT);

    return () => clearTimeout(timer);
  }, [history]);

  return (
    <div className={classes.backdrop}>
      <div className={classes.contentWrapper}>
        {showTitle && title && (
          <h1 className={classes.title}>{title}</h1>
        )}

        <GoogleLoader
          size={96}
          ringThickness={8}
          logoUrl={logo}
          bgColor={isDark ? "dark" : "light"}
        />

        <div style={{ textAlign: "center" }}>
          <p className={classes.message}>
            {showTimeout ? "Verificando sessão..." : message}
          </p>

          {showTimeout && (
            <p className={classes.timeoutMessage}>
              Isso está demorando mais que o esperado...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackdropLoading;
